/**
 * THE FREE INTERNSHIP'S WORDS (Justin, 2026-10-30; flagged for Miles). Every page, banner and notice that offers or
 * explains the free credit reads its words from here, so the site says one thing about it everywhere: the free
 * evaluation is now a free internship, a new organization's primary owner gets it once, and a code or a referral link
 * gives an extended internship. The figures come from ./trialTerms.ts (the server's mirror), never typed here, and
 * the copy names them in pr0 and weeks, never in dollars.
 *
 * A CARD STARTS IT (Miles, 2026-10-30, docs/PLAN-per-employee-pricing-20261030.md R8, R14): nothing is charged that
 * day, and the plan chosen at signup starts when it ends unless it is turned off in one tap. Said where the card is
 * asked for and where the plan is managed (Miles, 2026-11-02: "just expectations set at checkout", "and subscription
 * page q&a section on pricing page"): the card step (/subscription/trial), the subscription page and the pricing
 * page's answers read `cardSentence`. Every other line says "$0 today" and "unlimited human seats", never the card.
 *
 * ONE MONTH, PART TIME (Miles, 2026-11-02): the free internship is one month at 225 pr0, half a Prest0n employee's
 * month; the words say "1 month" and its pr0, never "part time" (a headline need not say it).
 *
 * Lowercase, in the site's voice; "Prest0n" keeps its capital. A page in the older sentence-case design capitalizes
 * the first letter where a sentence starts (sentenceCase below).
 */
import {
  INTERNSHIP,
  INTERNSHIP_WU_PER_MONTH,
  formatTermsLength,
  formatTermsLengthAdjective,
  formatTermsWu,
  type TrialTierTerms,
} from './trialTerms';
import { UNLIMITED_HUMAN_SEATS } from './priceHeadlineWords';

/** "225 pr0". */
const WU = formatTermsWu(INTERNSHIP);
/** "1 month". */
const LENGTH = formatTermsLength(INTERNSHIP);
/** "1-month". */
const LENGTH_ADJ = formatTermsLengthAdjective(INTERNSHIP);
/** People are free, the internship included (functions/src/rbac/seatLimit.ts: an armed internship seats everyone). */
const SEATS = UNLIMITED_HUMAN_SEATS;

export const INTERNSHIP_WORDS = {
  /** The lead action wherever a visitor is asked to start. */
  cta: 'get your free internship now',
  /** The same action where the full line does not fit (a button in a narrow row). */
  ctaShort: 'get your free internship',
  /** What it is called in a sentence. */
  name: 'free internship',
  /** What a code or a referral link gives instead. */
  extendedName: 'extended internship',
  /** The terms, one line: "a free 1-month internship · 225 pr0 of work · $0 today · unlimited human seats". */
  terms: `a free ${LENGTH_ADJ} internship · ${WU} of work · $0 today · ${SEATS}`,
  /**
   * The home page's hero line, under its actions: "a free 1-month internship · 225 pr0 · $0 today · unlimited human
   * seats" (Miles, 2026-11-02: "$0 today, unlimited human seats is all great"; no card in the hero). As long as the line
   * it replaced: scripts/check-hero-composition.mjs keeps the words 40px clear of the tablet with the chat docked at
   * 1280, and "of work" put them 36px away.
   */
  termsShort: `a free ${LENGTH_ADJ} internship · ${WU} · $0 today · ${SEATS}`,
  /** The same terms as a sentence: "a free internship is 1 month of Prest0n: 225 pr0 of work, credited up front, …". */
  termsSentence: `a free internship is ${LENGTH} of Prest0n: ${WU} of work, credited up front, with ${SEATS}.`,
  /**
   * What starts it and what follows it, said where the card is asked for: the card step, the subscription page and the
   * pricing page's answers (Miles, 2026-11-02). Never in a hero or a headline.
   */
  cardSentence: '$0 today: a card starts it, and the plan you choose starts when it ends unless you turn it off in one tap.',
  /** Who it is for. */
  who: 'it comes with a new organization, for its primary owner, once per person.',
  /** Where an internship ends. */
  after: 'when it ends, the plan you chose starts, unless you turned it off in one tap.',
  /** The pricing page's terms line beside the price: what it costs today, what it brings, and the seats. */
  cardLine: `$0 today · a free ${LENGTH_ADJ} internship with ${WU} of work · ${SEATS}`,
  /** A code redeemed or a referral link taken. */
  extended: 'an invitation code or a referral link gives an extended internship in its place.',
  /** A personal workspace with no plan: where its free internship is, and the three ways on. */
  personalStop: 'your free internship comes with a new organization: create yours, redeem a code, or hire a Prest0n.',
  /** Someone already in an organization: why they get none, and the ways on. */
  memberStop: 'free internships are for new organizations. your organization’s Prest0n is yours to use; a promo code or a hire adds more.',
  /**
   * A new organization whose owner did not get the free internship (the organization mint said so,
   * functions/src/internship/internshipEligibility.ts). One line for every reason: it never names a guard, an id or a code.
   */
  notGranted:
    'this organization starts without a free internship: free internships are once per person, for new organizations. ' +
    'hire a Prest0n or redeem a code to start work.',
  /** The "just me" fork at setup: a personal workspace comes without one. */
  justMe: 'a personal workspace starts without the free internship: it comes with a new organization.',
  /** Figures for a page that lays them out itself. */
  figures: { wu: WU, length: LENGTH, lengthAdjective: LENGTH_ADJ, ratePerMonth: `${INTERNSHIP_WU_PER_MONTH} pr0`, seats: SEATS },
} as const;

/**
 * A code's or a referral link's grant, in the same words: "an extended internship: 420 pr0 of work for 1 month".
 * `terms` is the grant's own (a code's terms, REFERRED_OFFER), never typed.
 */
export function extendedInternshipLine(terms: TrialTierTerms): string {
  return `an extended internship: ${formatTermsWu(terms)} of work for ${formatTermsLength(terms)}`;
}

/** "get your free internship now" → "Get your free internship now", for the older sentence-case pages. */
export function sentenceCase(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1);
}
